// Source of truth for the /marketing channel grid AND the per-channel
// product pages at /marketing/channels/:slug. The `productPage` block on
// each gated channel mirrors the Claude Design handoff at
// design_handoff_vesta_channel_pages/components/channels.js — accent
// tokens, hero copy, feature cards, best-for stats, and CTA copy.

export const MARKETING_CHANNELS = {
  google_ads_ai: {
    slug: "google_ads_ai",
    title: "Google Ads",
    subTitle: "Capture Intent. Drive Attendance.",
    description: "Our AI automatically creates and optimizes Google Ads campaigns to promote your events to people actively searching for things to do. Reach high-intent audiences at the right moment and turn searches into ticket sales.",
    bestFor: "Driving high-intent traffic and increasing event registrations with automated, conversion-focused campaigns.",
    implementations: ["google_ads_ai"],
    gradient: "linear-gradient(180deg, #4285F4 0%, #34A853 100%)",
    additionalText: "Connect your Google Ads account to launch AI-powered campaigns in minutes.",
    additionalGradient: "linear-gradient(180deg, rgba(66, 133, 244, 0.8) 0%, rgba(52, 168, 83, 0.8) 100%)",
    isComingSoon: false,
    accessType: "subscription",
    productPage: {
      heroVariant: "google-search",
      badge: "Google Ads",
      accent: "#4285F4",
      accent2: "#34A853",
      accentSoft: "rgba(66,133,244,.12)",
      accentLine: "rgba(66,133,244,.20)",
      ctaGlow: "rgba(66,133,244,.22)",
      ctaShadow: "0 8px 20px rgba(66,133,244,.35)",
      auroraTR: "radial-gradient(circle at center, rgba(140,220,255,.45) 0%, rgba(140,220,255,0) 62%)",
      auroraBL: "radial-gradient(circle at center, rgba(80,220,140,.32) 0%, rgba(80,220,140,0) 60%)",
      textGradient: "linear-gradient(90deg,#fff 0%,#D7E8FF 55%,#BDF0CF 100%)",
      bestForBg:
        "radial-gradient(120% 100% at 100% 0%, rgba(120,200,255,.35) 0%, transparent 55%)," +
        "radial-gradient(80% 100% at 0% 100%, rgba(80,220,150,.35) 0%, transparent 55%)," +
        "linear-gradient(135deg, #1967D2 0%, #4285F4 45%, #34A853 100%)",
      bestForShadow: "0 24px 56px rgba(66,133,244,.28), 0 8px 20px rgba(15,23,42,.12)",
      hero: {
        headlineStart: "Fill your events with people already searching on ",
        headlineSpan: "Google",
        headlineEnd: ".",
        sub: "Vesta's AI writes the ads, picks the keywords, and manages the bids, so every event shows up when your ideal attendees are actively looking for things to do.",
        trust: ["Launches in minutes", "Managed end-to-end", "No ad experience required"],
      },
      features: {
        eyebrow: "Everything You Need",
        headlineStart: "A full-stack Google Ads team, ",
        headlineAccent: "running on AI",
        headlineEnd: ".",
        intro: "From keyword research to real-time bid management, Vesta handles the three things that actually move tickets on Google.",
        cards: [
          { icon: "pen", title: "AI Campaign Creation",
            description: "Describe your event and our AI generates targeted ad copy, selects high-intent keywords, and sets up campaigns. No blank page, no agency, no ad experience needed.",
            bullets: ["Search + Display ad variants", "Auto-matched to event keywords", "Geo-targeted to your venue"] },
          { icon: "trending-up", title: "Smart Budget Optimization",
            description: "Our AI continuously adjusts bids and budgets to maximize your return on ad spend across search and display networks.",
            bullets: ["Real-time bid adjustments", "Budget shifted to top performers", "Dayparting for peak search hours"] },
          { icon: "chart", title: "Performance Dashboard",
            description: "See exactly what each campaign is doing: impressions, clicks, cost per registration, and conversions, unified with the rest of Vesta.",
            bullets: ["Cost-per-registration", "Keyword + ad group breakdowns", "Attribution back to Vesta checkout"] },
        ],
      },
      bestForBlock: {
        eyebrow: "Best For",
        headline: "Events that need to capture high-intent demand.",
        body: "When someone searches 'concerts near me this weekend' or 'food festival Chicago,' they're ready to buy. Google Ads puts your event at the top of those searches, with ads written by AI that match the intent behind the query. No wasted impressions on people who aren't looking.",
        stats: [
          { k: "Campaign goes live", v: "In minutes", sub: "not days" },
          { k: "Keywords managed", v: "50 – 200+", sub: "per event" },
          { k: "Optimized for", v: "Ticket sales", sub: "not just clicks" },
        ],
      },
      cta: {
        eyebrow: "Ready When You Are",
        headline: "Let's put your next event at the top of Google.",
        body: "Book a 20-minute walkthrough and we'll show you what a Google Ads campaign would look like for an event on your calendar.",
      },
    },
  },
  social_media_ai: {
    slug: "social_media_ai",
    title: "Social Media",
    subTitle: "Engaging Posts, Written for You",
    description: "Our AI creates high-converting social media posts tailored to your event, optimized for platforms like Facebook and Instagram. Keep your audience engaged with compelling content that drives ticket sales.",
    bestFor: "Executing on a highly engaging social media strategy on auto-pilot.",
    implementations: ["social_media_ai"],
    gradient: "linear-gradient(180deg, #34C759 0%, #193761 100%)",
    additionalText: "Connect Facebook and grant 'Manage Posts' permission to get started!",
    additionalGradient: "linear-gradient(180deg, rgb(52 199 89 / 80%) 0%, rgb(25 55 97 / 80%) 100%)",
    isComingSoon: false,
    accessType: "subscription",
    productPage: {
      heroVariant: "social-post",
      badge: "Social Media",
      accent: "#34C759",
      accent2: "#193761",
      accentSoft: "rgba(52,199,89,.12)",
      accentLine: "rgba(52,199,89,.22)",
      ctaGlow: "rgba(52,199,89,.22)",
      ctaShadow: "0 8px 20px rgba(52,199,89,.35)",
      auroraTR: "radial-gradient(circle at center, rgba(140,240,170,.40) 0%, rgba(140,240,170,0) 62%)",
      auroraBL: "radial-gradient(circle at center, rgba(80,180,220,.30) 0%, rgba(80,180,220,0) 60%)",
      textGradient: "linear-gradient(90deg,#fff 0%,#C8F6D7 55%,#A8E1F2 100%)",
      bestForBg:
        "radial-gradient(120% 100% at 100% 0%, rgba(120,240,170,.38) 0%, transparent 55%)," +
        "radial-gradient(80% 100% at 0% 100%, rgba(60,120,200,.35) 0%, transparent 55%)," +
        "linear-gradient(135deg, #0E2240 0%, #1F5A85 45%, #34C759 120%)",
      bestForShadow: "0 24px 56px rgba(25,55,97,.32), 0 8px 20px rgba(15,23,42,.12)",
      hero: {
        headlineStart: "Automated social media. Created, scheduled, and posted by ",
        headlineSpan: "AI",
        headlineEnd: ".",
        sub: "Stop staring at a blank screen. Vesta's AI writes engaging posts for every event, schedules them at the right times, and publishes to Facebook and Instagram, so your social presence runs itself.",
        trust: ["Posts written in seconds", "Multi-platform publishing", "No social media manager needed"],
      },
      features: {
        eyebrow: "Everything You Need",
        headlineStart: "A full-stack social media team, ",
        headlineAccent: "running on AI",
        headlineEnd: ".",
        intro: "From the first draft to the last scheduled post, Vesta handles the three things that keep your event top-of-feed.",
        cards: [
          { icon: "pen", title: "AI-Generated Content",
            description: "Tailored posts for each event with compelling copy, hashtags, and calls-to-action that actually drive engagement, written in seconds, not hours.",
            bullets: ["Event-specific captions + hashtags", "Multiple tone options (professional, casual, hype)", "Optimized for each platform's algorithm"] },
          { icon: "share", title: "Multi-Platform Publishing",
            description: "Post to Facebook Pages and Instagram simultaneously. One click, both platforms, formatted correctly for each.",
            bullets: ["Facebook + Instagram in one click", "Auto-formatted per platform", "Connected to your business pages"] },
          { icon: "calendar", title: "Content Calendar",
            description: "AI schedules posts at optimal times leading up to your event, building momentum and driving registrations on autopilot.",
            bullets: ["AI-optimized posting times", "Pre-event countdown sequences", "Post-event recap suggestions"] },
        ],
      },
      bestForBlock: {
        eyebrow: "Best For",
        headline: "Event organizers who want a social media presence without the time sink.",
        body: "You know you should be posting about your events, but you don't have time to write captions, find hashtags, and schedule posts across platforms. Our AI does the creative work, and you just review and approve. Most customers go from zero social presence to a consistent posting schedule in their first week.",
        stats: [
          { k: "Posts per event", v: "8 – 15", sub: "across platforms" },
          { k: "Time to create", v: "Under 30 sec", sub: "per post" },
          { k: "Platforms supported", v: "Facebook + IG", sub: "from one place" },
        ],
      },
      cta: {
        eyebrow: "Ready When You Are",
        headline: "Let AI handle your event's social media.",
        body: "Book a 20-minute walkthrough and we'll show you what AI-generated social content looks like for one of your upcoming events.",
      },
    },
  },
  meta_ads_ai: {
    slug: "meta_ads_ai",
    title: "Meta Ads",
    subTitle: "Maximize Your ROAS on Facebook & Instagram",
    description: "Our AI creates and manages high-performing ad campaigns on Facebook and Instagram. Reach your ideal attendees with precision targeting and creative that converts.",
    bestFor: "Maximizing organic reach with high return on ad spend (ROAS).",
    implementations: ["meta_ads_ai"],
    gradient: "linear-gradient(180deg, #6A3AFB 0%, #822395 100%)",
    additionalText: "Coming Soon",
    additionalGradient: "linear-gradient(180deg, rgba(106, 58, 251, 0.8) 0%, rgba(130, 35, 149, 0.8) 100%)",
    isComingSoon: false,
    accessType: "subscription",
    productPage: {
      heroVariant: "ad-preview",
      badge: "Meta Ads",
      accent: "#6A3AFB",
      accent2: "#822395",
      accentSoft: "rgba(106,58,251,.12)",
      accentLine: "rgba(106,58,251,.18)",
      ctaGlow: "rgba(106,58,251,.22)",
      ctaShadow: "0 8px 20px rgba(106,58,251,.35)",
      auroraTR: "radial-gradient(circle at center, rgba(218,180,255,.38) 0%, rgba(218,180,255,0) 62%)",
      auroraBL: "radial-gradient(circle at center, rgba(255,80,180,.28) 0%, rgba(255,80,180,0) 60%)",
      textGradient: "linear-gradient(90deg,#fff 0%,#E8DCFF 55%,#FFB5EC 100%)",
      bestForBg:
        "radial-gradient(120% 100% at 100% 0%, rgba(255,108,217,.35) 0%, transparent 55%)," +
        "radial-gradient(80% 100% at 0% 100%, rgba(183,148,255,.35) 0%, transparent 55%)," +
        "linear-gradient(135deg, #4A1CB8 0%, #6A3AFB 40%, #822395 100%)",
      bestForShadow: "0 24px 56px rgba(106,58,251,.28), 0 8px 20px rgba(15,23,42,.12)",
      hero: {
        headlineStart: "Sell out your events with AI designed to maximize ",
        headlineSpan: "Meta's advertising engine",
        headlineEnd: ".",
        sub: "Vesta's AI builds the ads, picks the audiences, and manages your budget, so every event gets in front of the people most likely to buy tickets.",
        trust: ["Launches in minutes", "Managed end-to-end", "No ad experience required"],
      },
      features: {
        eyebrow: "Everything You Need",
        headlineStart: "A full-stack Meta Ads team, ",
        headlineAccent: "running on AI",
        headlineEnd: ".",
        intro: "From the first creative draft to the last optimization pass, Vesta handles the three things that actually move tickets.",
        cards: [
          { icon: "pen", title: "AI Ad Creation",
            description: "Generate scroll-stopping creative and copy, tuned to each event. No blank page, no agency, no ad experience needed.",
            bullets: ["Headline + body + CTA variants", "Auto-resized for Feed, Reels, Stories", "On-brand to your event vibe"] },
          { icon: "target", title: "Audience Targeting",
            description: "Our AI builds the right audience for every event: interests, geo-radius, demographics, plus lookalikes of past attendees.",
            bullets: ["Interest + behavioral targeting", "Geo-radius around your venue", "Lookalikes from your ticket-buyers"] },
          { icon: "chart", title: "Campaign Analytics",
            description: "See exactly what each campaign is doing for each event: impressions, cost per ticket, and ROAS, unified with the rest of Vesta.",
            bullets: ["Cost-per-ticket-sold", "Creative + audience breakdowns", "Attribution back to Vesta checkout"] },
        ],
      },
      bestForBlock: {
        eyebrow: "Best For",
        headline: "One-off events that need to sell tickets, now.",
        body: "Most of our customers aren't running the same event every month. They have one night, one show, one window to fill the room. Our AI treats every event like its own campaign: fresh creative, fresh audience, budget pushed hard in the weeks that matter, then shut off cleanly when the doors close.",
        stats: [
          { k: "Campaign goes live", v: "In minutes", sub: "not days" },
          { k: "Creative variants", v: "6 – 12", sub: "tested automatically" },
          { k: "Managed through", v: "Event day", sub: "hands-off" },
        ],
      },
      cta: {
        eyebrow: "Ready When You Are",
        headline: "Let's put your next event in front of the right crowd.",
        body: "Book a 20-minute walkthrough and we'll show you what a Meta Ads campaign would look like for an event on your calendar.",
      },
    },
  },
  email_marketing_ai: {
    slug: "email_marketing_ai",
    title: "Event Email Platform",
    subTitle: "Turn Your Event Listings Into Ready-to-Send Campaigns",
    description: "Create announcements, reminders, and roundups using synced event blocks that pull directly from Event Vesta. Build campaigns manually or automate them when you're ready. No copy-pasting required.",
    bestFor: "Organizers who want event-aware email marketing with full control.",
    implementations: ["evvnt_email_blast"],
    gradient: "linear-gradient(180deg, #FF7322 0%, #8E3215 100%)",
    additionalText: "Coming Soon",
    additionalGradient: "linear-gradient(rgb(255 115 34 / 80%) 0%, rgb(142 50 21 / 80%) 100%)",
    isComingSoon: false,
    accessType: "subscription",
    productPage: {
      heroVariant: "email-preview",
      badge: "Event Email Platform",
      accent: "#FF7322",
      accent2: "#8E3215",
      accentSoft: "rgba(255,115,34,.12)",
      accentLine: "rgba(255,115,34,.22)",
      ctaGlow: "rgba(255,115,34,.22)",
      ctaShadow: "0 8px 20px rgba(255,115,34,.35)",
      auroraTR: "radial-gradient(circle at center, rgba(255,200,140,.45) 0%, rgba(255,200,140,0) 62%)",
      auroraBL: "radial-gradient(circle at center, rgba(210,120,60,.32) 0%, rgba(210,120,60,0) 60%)",
      textGradient: "linear-gradient(90deg,#fff 0%,#FFE0C2 55%,#FFCB8B 100%)",
      bestForBg:
        "radial-gradient(120% 100% at 100% 0%, rgba(255,180,120,.38) 0%, transparent 55%)," +
        "radial-gradient(80% 100% at 0% 100%, rgba(210,120,60,.35) 0%, transparent 55%)," +
        "linear-gradient(135deg, #5B2412 0%, #B8451C 45%, #FF7322 110%)",
      bestForShadow: "0 24px 56px rgba(255,115,34,.28), 0 8px 20px rgba(15,23,42,.12)",
      hero: {
        headlineStart: "Automated email marketing that's synced to ",
        headlineSpan: "your events",
        headlineEnd: ".",
        sub: "Build campaigns with synced event blocks that always have the right name, date, venue, and ticket link. Announcements, reminders, and roundups, with no copy-pasting and no outdated info.",
        trust: ["Synced to your events", "Build or automate", "No email marketing experience needed"],
      },
      features: {
        eyebrow: "Everything You Need",
        headlineStart: "Email marketing, ",
        headlineAccent: "synced to your events",
        headlineEnd: ".",
        intro: "From the first announcement to the post-event recap, Vesta keeps your campaigns accurate without the copy-paste.",
        cards: [
          { icon: "zap", title: "Synced Event Blocks",
            description: "Drop event blocks into your emails that automatically pull name, date, venue, and ticket links directly from Vesta. Update the event, and the email updates too.",
            bullets: ["Auto-synced event details", "No copy-paste, no stale info", "Drag-and-drop email builder"] },
          { icon: "clock", title: "Campaign Automation",
            description: "Set up automated announcement and reminder sequences that send at exactly the right time, or build campaigns manually when you want full control.",
            bullets: ["Pre-event announcement sequences", "Reminder emails before the date", "Manual campaigns when you want control"] },
          { icon: "users", title: "Audience Management",
            description: "Manage contacts, segment your audience by event history and interests, and track opens, clicks, and conversions per event.",
            bullets: ["Contact management + import", "Segment by event attendance", "Per-event open + click tracking"] },
        ],
      },
      bestForBlock: {
        eyebrow: "Best For",
        headline: "Organizers who want email marketing that actually knows their events.",
        body: "Most email platforms make you copy-paste event details into every campaign. Vesta's email platform syncs directly with your event listings, so the name, date, venue, and ticket link are always right. Build a campaign once, and it stays accurate even if you change the event details later. No more sending emails with the wrong date.",
        stats: [
          { k: "Event details synced", v: "Automatically", sub: "always up-to-date" },
          { k: "Campaign types", v: "3 built-in", sub: "announce, remind, recap" },
          { k: "Setup time", v: "Under 5 min", sub: "per campaign" },
        ],
      },
      cta: {
        eyebrow: "Ready When You Are",
        headline: "Send event emails that automatically get the details right.",
        body: "Book a 20-minute walkthrough and we'll show you how event-synced email campaigns work with your actual events.",
      },
    },
  },
  press_release_ai: {
    slug: "press_release_ai",
    title: "Press Release AI",
    subTitle: "Let AI Craft the Perfect Story",
    description: "Struggling to write the perfect event announcement? Our AI-powered press release tool generates professional, media-ready content capable of getting your event noticed by news outlets and publications.",
    bestFor: "News-worthy events that want media attention.",
    implementations: ["press_release"],
    gradient: "linear-gradient(180deg, #FF9500 0%, #999900 100%)",
    additionalText: "Coming Soon!",
    additionalGradient: "linear-gradient(rgb(255 149 0 / 80%) 0%, rgb(153 153 0 / 80%) 100%)",
    isComingSoon: false,
    accessType: "subscription",
    productPage: {
      heroVariant: "press-release",
      badge: "Press Release AI",
      accent: "#FF9500",
      accent2: "#999900",
      accentSoft: "rgba(255,149,0,.12)",
      accentLine: "rgba(255,149,0,.22)",
      ctaGlow: "rgba(255,149,0,.22)",
      ctaShadow: "0 8px 20px rgba(255,149,0,.35)",
      auroraTR: "radial-gradient(circle at center, rgba(255,210,120,.45) 0%, rgba(255,210,120,0) 62%)",
      auroraBL: "radial-gradient(circle at center, rgba(200,200,90,.32) 0%, rgba(200,200,90,0) 60%)",
      textGradient: "linear-gradient(90deg,#fff 0%,#FFE5B2 55%,#E6DB8A 100%)",
      bestForBg:
        "radial-gradient(120% 100% at 100% 0%, rgba(255,200,100,.38) 0%, transparent 55%)," +
        "radial-gradient(80% 100% at 0% 100%, rgba(200,200,80,.35) 0%, transparent 55%)," +
        "linear-gradient(135deg, #665900 0%, #C27800 45%, #FF9500 110%)",
      bestForShadow: "0 24px 56px rgba(255,149,0,.28), 0 8px 20px rgba(15,23,42,.12)",
      hero: {
        headlineStart: "Professional press releases for your events, written and ",
        headlineSpan: "distributed by AI",
        headlineEnd: ".",
        sub: "Stop struggling with event announcements. Vesta's AI generates polished, publication-ready press releases that get your event noticed by news outlets, blogs, and local media.",
        trust: ["Publication-ready in minutes", "Proper AP-style formatting", "No PR experience needed"],
      },
      features: {
        eyebrow: "Everything You Need",
        headlineStart: "A full PR desk, ",
        headlineAccent: "running on AI",
        headlineEnd: ".",
        intro: "From the first draft to media distribution, Vesta handles the parts of PR that used to require an agency.",
        cards: [
          { icon: "pen", title: "AI Writing",
            description: "Enter your event details and our AI generates a polished, publication-ready press release with proper structure, quotes, and formatting, in under a minute.",
            bullets: ["AP-style structure + formatting", "Auto-generated quotes + boilerplate", "Multiple tone options (formal, conversational)"] },
          { icon: "globe", title: "Media Distribution",
            description: "Distribute your press release to relevant local and industry media outlets to maximize coverage for your event.",
            bullets: ["Local media outlet targeting", "Industry-specific distribution", "One-click send to media lists"] },
          { icon: "layers", title: "Multiple Formats",
            description: "Get your press release in formats ready for email pitches, website publishing, and wire distribution, all from one AI-generated draft.",
            bullets: ["Email pitch-ready version", "Website/blog-ready HTML", "Wire service format"] },
        ],
      },
      bestForBlock: {
        eyebrow: "Best For",
        headline: "News-worthy events that deserve media attention.",
        body: "Festival announcements, venue openings, charity galas, milestone celebrations: these are the events that local media actually wants to cover. But most organizers don't have a PR person to write the release. Our AI writes it in the format journalists expect, with the details they need, so your event has the best chance of getting picked up.",
        stats: [
          { k: "Draft ready in", v: "Under 1 min", sub: "from your event details" },
          { k: "Revisions included", v: "Unlimited", sub: "refine until perfect" },
          { k: "Output formats", v: "3 included", sub: "email, web, wire" },
        ],
      },
      cta: {
        eyebrow: "Ready When You Are",
        headline: "Get your event the media attention it deserves.",
        body: "Book a 20-minute walkthrough and we'll generate a sample press release for one of your upcoming events, live.",
      },
    },
  },
  email_list: {
    slug: "email_list",
    title: "Partner Email Blast",
    subTitle: "Promote Your Event to a Local Opt-In Audience",
    description: "Vesta's local event email list helps you connect with engaged subscribers who are interested in events like yours. We combine our own curated list with highly relevant local media subscribers to drive real ticket sales.",
    bestFor: "Expanding reach with a targeted third-party audience in your market.",
    implementations: ["evvnt_email_blast"],
    gradient: "linear-gradient(180deg, #FF2D55 0%, #995A1B 100%)",
    additionalText: "You've already used this channel for this event.",
    additionalGradient: "linear-gradient(rgb(255 45 85 / 80%) 0%, rgb(153 90 27 / 80%) 100%)",
    accessType: "one-off"
  },
  featured_placements: {
    slug: "featured_placements",
    title: "Featured Placements",
    subTitle: "Put Your Event in the Spotlight",
    description: "Get prime placement on The CW33 and Patch.com website's. Featured listings ensure your event gets seen by thousands of potential attendees looking for things to do every week.",
    bestFor: "Maximizing exposure with premium visibility.",
    implementations: ["patch_featured_placement", "cityspark_featured_placement"],
    gradient: "linear-gradient(180deg, #004DA0 0%, #00C7BE 100%)",
    additionalText: "You've already used this channel for this event.",
    additionalGradient: "linear-gradient(rgb(0 77 160 / 80%) 0%, rgb(0 199 190 / 80%) 100%)",
    accessType: "one-off"
  },
  event_calendars: {
    slug: "event_calendars",
    title: "Event Calendars",
    subTitle: "Go Where People Are Already Looking",
    description: "Event calendars attract people who are actively searching for things to do in your city. Listing your event on Google and top local sites puts it in front of the right audience at the right time.",
    bestFor: "Maximizing organic reach with high return on ad spend (ROAS).",
    implementations: ["evvnt_email_blast"],
    gradient: "linear-gradient(180deg, #A500C6 0%, #0F003B 100%)",
    additionalText: "You've already used this channel for this event.",
    additionalGradient: "linear-gradient(167.5deg, rgba(165, 0, 198, 0.8) 0%, rgba(15, 0, 59, 0.8) 100%)",
    accessType: "tier"
  },
};

export const MARKETING_CHANNELS_ARRAY = Object.values(MARKETING_CHANNELS);
